// spendTypes.ts — spend per sheet sync (ADMIN ONLY). Mirrors models/syncSpend.go.
// Amounts travel as paise (₹1 = 100). The user types rupees; the API stores paise.

import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { UserRef } from '../../SheetsManager/parts/SheetImportDetailsModal';

export interface SpendSplit {
  campaign_key: string; // "id:…" | "name:…" | "none"
  campaign_name?: string;
  amount_paise: number;
  leads_count: number;
}

export interface SpendSnapshot {
  amount_paise: number;
  gst_included: boolean;
  split_mode?: 'auto' | 'manual';
  splits?: SpendSplit[];
}

export interface SpendChange {
  action: 'set' | 'update';
  before?: SpendSnapshot | null;
  after: SpendSnapshot;
  reason?: string;
  by?: UserRef | null;
  at: string;
}

export interface SyncSpend {
  id: string;
  sheet_import_id: string;
  sheet_id?: string;
  amount_paise: number;
  gst_included: boolean;
  amount_incl_gst_paise: number;
  amount_excl_gst_paise: number;
  leads_count: number;
  cost_per_lead_incl_gst: number;
  cost_per_lead_excl_gst: number;
  splits: SpendSplit[];
  split_mode: 'auto' | 'manual';
  leads_added_at: string;
  created_by?: UserRef | null;
  created_at: string;
  updated_by?: UserRef | null;
  updated_at?: string | null;
  history: SpendChange[] | null;
}

/** What the push step / edit form sends. Amounts in rupees. */
export interface SpendInput {
  amount: number;
  gst_included: boolean;
  splits?: { campaign_key: string; amount: number }[];
  reason?: string;
}

export interface SpendForSync {
  data: SyncSpend | null;
  /** Campaigns in the sync (staged rows before push, leads after). */
  campaigns: SpendSplit[];
  leads_count: number;
}

// Errors from spend endpoints come as "message" (not "error") so the axios
// interceptor never mistakes them for a login problem.
export const spendError = (e: any, fallback: string): string =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;

export const spendApi = {
  get: (importId: string) =>
    apiClient.get<{ success: boolean } & SpendForSync>(`/v1/sheet-imports/${importId}/spend`).then((r) => r.data),
  set: (importId: string, body: SpendInput) =>
    apiClient.put<{ success: boolean; data: SyncSpend }>(`/v1/sheet-imports/${importId}/spend`, body).then((r) => r.data.data),
  /** Summaries for Sync History, keyed by import id. */
  list: (importIds: string[]) =>
    apiClient
      .get<{ success: boolean; data: Record<string, SyncSpend> }>(`/v1/sheet-imports/spends?ids=${importIds.join(',')}`)
      .then((r) => r.data.data ?? {}),
};

// ── Money ────────────────────────────────────────────────────────────────────

/** ₹6,240 or ₹6,240.50 — Indian digit grouping (₹1,00,000). */
export const fmtINR = (paise: number | null | undefined, opts: { decimals?: 'auto' | 0 | 2 } = {}): string => {
  if (paise === null || paise === undefined || isNaN(paise)) return '—';
  const rupees = paise / 100;
  const whole = Number.isInteger(rupees);
  const d = opts.decimals === 0 ? 0 : opts.decimals === 2 ? 2 : whole ? 0 : 2;
  return '₹' + rupees.toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
};

/** Parse what the user typed ("6,240.5", "₹ 6240") into paise, or null. */
export const parseRupees = (text: string): number | null => {
  const clean = text.replace(/[₹,\s]/g, '');
  if (!clean) return null;
  if (!/^\d+(\.\d{0,2})?$/.test(clean)) return NaN;
  return Math.round(parseFloat(clean) * 100);
};

export const GST_RATE = 0.18;

export const inclExcl = (paise: number, gstIncluded: boolean) =>
  gstIncluded
    ? { incl: paise, excl: Math.round(paise / (1 + GST_RATE)) }
    : { incl: Math.round(paise * (1 + GST_RATE)), excl: paise };

/** Split a total by each campaign's share of leads (parts always add up). */
export const splitByLeads = (campaigns: SpendSplit[], totalPaise: number): Record<string, number> => {
  const leads = campaigns.reduce((a, c) => a + c.leads_count, 0);
  const out: Record<string, number> = {};
  if (!campaigns.length) return out;
  if (leads === 0) {
    campaigns.forEach((c, i) => (out[c.campaign_key] = i === 0 ? totalPaise : 0));
    return out;
  }
  let given = 0;
  const rems = campaigns.map((c) => {
    const share = totalPaise * c.leads_count;
    out[c.campaign_key] = Math.floor(share / leads);
    given += out[c.campaign_key];
    return { key: c.campaign_key, r: share % leads };
  });
  rems.sort((a, b) => b.r - a.r);
  for (let k = 0; given < totalPaise; k++) {
    out[rems[k % rems.length].key]++;
    given++;
  }
  return out;
};

export const campaignLabel = (s: Pick<SpendSplit, 'campaign_key' | 'campaign_name'>) =>
  s.campaign_name || (s.campaign_key.startsWith('id:') ? s.campaign_key.slice(3) : s.campaign_key.startsWith('name:') ? s.campaign_key.slice(5) : 'No campaign');
